import { useMutation } from '@tanstack/react-query';
import { fetchValidateUserPaymentDetails } from './service';
import { UseValidateUserPaymentDetailsProps } from './props';
import { useMemo } from 'react';
import { groupBy } from '@starsoft/common/utils';
import { UserPaymentDetailsFormFields } from '@starsoft/common/enums';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { ValidateUserPaymentDetailsInputDto } from '@starsoft/common/dto';
import { AxiosInstance } from 'axios';

/**
 * Custom hook to validate user payment details
 *
 * This hook handles validating payment details for a user, managing the mutation state,
 * and mapping validation errors to specific form fields. It uses React Query's mutation
 * capabilities for state management.
 *
 * @param {UseValidateUserPaymentDetailsProps} props - Optional configuration object
 * @param {Function} props.onSuccess - Optional callback function to execute on successful validation
 *
 * @returns {Object} An object containing:
 *   - isPending: Boolean indicating if validation is in progress
 *   - validationResult: The validation result data
 *   - validateUserPaymentDetails: Function to trigger the validation
 *     @param {ValidateUserPaymentDetailsInputDto} data - The payment details to validate
 *   - errorMapping: Map of validation errors grouped by form fields
 */
export function useValidateUserPaymentDetails(
  props?: UseValidateUserPaymentDetailsProps,
) {
  const { onSuccess } = props ?? {};
  const axiosInstance: AxiosInstance = useAxiosInstance();

  const { mutate, isPending, data } = useMutation({
    retry: 1,
    onSuccess,
    mutationFn: (data: ValidateUserPaymentDetailsInputDto) =>
      fetchValidateUserPaymentDetails(data, axiosInstance),
  });

  // Create and memoize a map of validation errors grouped by form fields
  const errorMapping: Map<UserPaymentDetailsFormFields, string[]> =
    useMemo(() => {
      const errors = data?.errors ?? [];
      const parsedErrors = errors.map(error => {
        return {
          ...error,
          field: error?.code?.includes('bank_account')
            ? UserPaymentDetailsFormFields.BankAccountNumber
            : undefined,
        };
      });

      return groupBy({
        records: parsedErrors,
        key: 'field',
        valueKey: 'description',
      });
    }, [data]);

  return {
    isPending,
    validationResult: data,
    validateUserPaymentDetails: mutate,
    errorMapping,
  };
}
